import {
  BasicResponsiveImage,
  Collapsible,
  CollapsibleContent,
  CollapsibleSummary,
  CollapsibleSummaryButton,
  Divider,
  Li,
  Link,
  StandardTile,
  StandardTileContents,
  StandardTileDescription,
  StandardTileImage,
  StandardTileName,
  StandardTileOverlay,
  Ul,
} from '@vp/swan'
import type { NavigationCategory, NavigationItem } from '../../../../types/navigation'
import { MAX_THIRD_LEVEL_ITEMS } from '../../../../constants/config'
import { getHeaderTracker } from '../../../../tracking'
import { CATEGORY_SECTION, MERCHANDISING_SECTION, PROMO_TILE, TEXT_LAYOUT_LINK, TILE_LIST_TILE, TOP_NAV } from '../../../../constants/analytics'
import type { NavigationSectionType } from '../../../../types/tracking'
import BadgeWithText from '../../BadgeWithText'
import { Badge } from '../../../common/Badge'
import { useNavContext } from '../../../../context/NavContextProvider'
import { ComponentType } from '../../../../types/components-map'

type NavCategoryPanelProps = Readonly<{
  activeNavData: NavigationCategory
}>

type NavItemProps = Readonly<{
  navItem: NavigationItem
  parent: NavigationCategory
  navigationSection?: NavigationSectionType
}>

type NavSubItemProps = Omit<NavItemProps, 'parent'> &
  Readonly<{
    isBold?: boolean
    parentNavigationDetails: string[]
  }>

const hasChildren = (navItem: NavigationItem): boolean => Boolean(navItem.children?.length)

/**
 * Determines whether a nav item is in the category or merchandising section.
 */
const getNavigationSection = (currentIndex: number, mxStartIndex: number): NavigationSectionType => {
  if (mxStartIndex === -1) return CATEGORY_SECTION
  return currentIndex < mxStartIndex ? CATEGORY_SECTION : MERCHANDISING_SECTION
}

/**
 * Tracks a navigation link click with the correct section.
 */
const trackNavLinkClick = (
  parentNavigationDetails: string[],
  navItem: NavigationItem,
  linkType: typeof TEXT_LAYOUT_LINK | typeof PROMO_TILE | typeof TILE_LIST_TILE = TEXT_LAYOUT_LINK,
  navigationSection: NavigationSectionType = CATEGORY_SECTION,
) => {
  getHeaderTracker().trackLink({
    subSection: TOP_NAV,
    navigationDetailPath: [...parentNavigationDetails, navItem.analyticsId || navItem.text],
    navigationSection,
    href: navItem.url,
    linkType,
  })
}

/**
 * Panel displaying the children of a selected navigation category in mobile menu.
 * Supports collapsible subcategories, visual promo tiles, and MX section tracking.
 */
export default function NavCategoryPanel({ activeNavData }: NavCategoryPanelProps) {
  const { isMxNav } = useNavContext()
  const mxStartIndex = activeNavData.children.findIndex((node) => node.startOfMx)

  return (
    <Ul skin="minimal" style={{ gap: 0 }}>
      {activeNavData.seeAllCategory && activeNavData.url && isMxNav && (
        <Li mt={4}>
          <Link
            skin="unstyled"
            href={activeNavData.url}
            fontSkin="body-standard-bold"
            py={4}
            onClick={() => trackNavLinkClick([activeNavData.analyticsId], activeNavData as unknown as NavigationItem)}
            data-position={0}
            data-translations={[activeNavData.analyticsId, activeNavData.analyticsId].join('|')}
            data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
          >
            <BadgeWithText badge={activeNavData.badge} text={activeNavData.seeAllCategory} />
          </Link>
        </Li>
      )}
      {activeNavData.children.map((nav, index) => (
        <Li key={nav.id}>
          <NavCategoryItem
            navItem={{ ...nav, itemPosition: index + 1 }}
            parent={activeNavData}
            isVisual={activeNavData.isVisual || nav.isVisual}
            navigationSection={getNavigationSection(index, mxStartIndex)}
          />
        </Li>
      ))}
      {activeNavData.seeAllMinimal && activeNavData.url && !isMxNav && (
        <Li>
          <Link
            skin="unstyled"
            href={activeNavData.url}
            fontSkin="body-standard-bold"
            py={4}
            onClick={() => trackNavLinkClick([activeNavData.analyticsId], activeNavData as unknown as NavigationItem)}
            data-position={activeNavData.children.length + 1}
            data-translations={[activeNavData.analyticsId, activeNavData.analyticsId].join('|')}
            data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
          >
            <BadgeWithText badge={activeNavData.badge} text={activeNavData.seeAllMinimal} />
          </Link>
        </Li>
      )}
    </Ul>
  )
}

function NavCategoryItem({ navItem, parent, isVisual, navigationSection = CATEGORY_SECTION }: NavItemProps & { isVisual?: boolean }) {
  if (isVisual) {
    return <NavCategoryVisualTile navItem={navItem} parent={parent} navigationSection={navigationSection} />
  }

  if (!hasChildren(navItem)) {
    return (
      <NavCategorySubItem navItem={navItem} parentNavigationDetails={[parent.analyticsId, navItem.analyticsId]} navigationSection={navigationSection} isBold />
    )
  }

  return <CollapsibleNavItem navItem={navItem} parent={parent} navigationSection={navigationSection} />
}

function CollapsibleNavItem({ navItem, parent, navigationSection = CATEGORY_SECTION }: NavItemProps) {
  const { hoverSubNavItem, isMxNav } = useNavContext()
  const handleExpand = (expanded: boolean) => {
    if (expanded) {
      hoverSubNavItem(navItem.id, parent.id)
    }
  }
  return (
    <>
      {navItem.startOfMx && <Divider my={3} />}
      <Collapsible headingLevel="h3" m={0} onRequestExpandedChange={handleExpand}>
        <CollapsibleSummary>
          <CollapsibleSummaryButton px={0} py={4}>
            <BadgeWithText badge={navItem.badge} text={navItem.text} />
          </CollapsibleSummaryButton>
        </CollapsibleSummary>
        <CollapsibleContent pb={0}>
          <Ul skin="minimal" ml={2} pt={0}>
            {navItem.seeAllMinimal && navItem.url && isMxNav && (
              <Li>
                <Link
                  skin="unstyled"
                  width="full-width"
                  href={navItem.url}
                  py={4}
                  onClick={() => trackNavLinkClick([parent.analyticsId], navItem, TEXT_LAYOUT_LINK, navigationSection)}
                  data-position={navItem.itemPosition}
                  data-translations={[parent.analyticsId, navItem.analyticsId].join('|')}
                  data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
                >
                  <BadgeWithText badge={navItem.badge} text={navItem.seeAllMinimal} />
                </Link>
              </Li>
            )}
            {navItem.children.slice(0, MAX_THIRD_LEVEL_ITEMS).map((child, index) => (
              <NavCategorySubItem
                key={child.id}
                navItem={{ ...child, itemPosition: index + 1 }}
                parentNavigationDetails={[parent.analyticsId, navItem.analyticsId]}
                navigationSection={navigationSection}
              />
            ))}
          </Ul>
        </CollapsibleContent>
      </Collapsible>
    </>
  )
}

function NavCategorySubItem({ navItem, parentNavigationDetails, navigationSection = CATEGORY_SECTION, isBold }: NavSubItemProps) {
  return (
    <Link
      skin="unstyled"
      href={navItem.url}
      width="full-width"
      fontSkin={isBold ? 'body-standard-bold' : 'body-standard'}
      py={4}
      onClick={() => trackNavLinkClick(parentNavigationDetails, navItem, TEXT_LAYOUT_LINK, navigationSection)}
      data-position={navItem.itemPosition}
      data-translations={[...parentNavigationDetails, navItem.analyticsId].join('|')}
      data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
    >
      <BadgeWithText badge={navItem.badge} text={navItem.text} />
    </Link>
  )
}

function NavCategoryVisualTile({ navItem, parent, navigationSection = CATEGORY_SECTION }: NavItemProps) {
  const handleClick = () => trackNavLinkClick([parent.analyticsId], navItem, parent.isVisual ? TILE_LIST_TILE : PROMO_TILE, navigationSection)

  return (
    <StandardTile
      layout="vertical"
      imageWidth="full-bleed"
      pt={6}
      render={(props) => (
        <Link href={navItem.url} className={props.className} onClick={handleClick} onKeyDown={handleClick}>
          {props.children}
        </Link>
      )}
    >
      {navItem.badge && (
        <StandardTileOverlay>
          <Badge badge={navItem.badge} />
        </StandardTileOverlay>
      )}
      <StandardTileImage>
        <BasicResponsiveImage aspectRatio={1} alt={navItem.text} src={navItem?.image?.url} loading="lazy" decoding="async" />
      </StandardTileImage>
      <StandardTileContents>
        <StandardTileName>{navItem.text}</StandardTileName>
        <StandardTileDescription>{navItem.description}</StandardTileDescription>
      </StandardTileContents>
    </StandardTile>
  )
}
